import { useLazyQuery, useApolloClient } from "@apollo/client";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Button from "@/components/Button";
import Dialog from "@/components/Dialog";
import Icon from "@/components/Icon";
import TextField from "@/components/TextField";
import {
  AlgoliaSearchResults,
  type PersonHit,
  createPersonOnlyHitRenderer,
  isPersonExactMatch
} from "@/components/AlgoliaSearch";
import { useNavigate, type Path } from "@/router";
import { useLocation } from "react-router-dom";
import classNames from "classnames";
import { formatPhoneNumber, normalizePhoneNumber } from "@/lib/phoneHelpers";
import { useIsCompactLayout } from "@/hooks/useIsCompactLayout";
import { useFlags } from "@/hooks/useFeatureFlags";
import {
  createActiveCallSearchIndex,
  searchPeopleForPhone,
  searchPeopleForQuery,
  type ActiveCallPersonSearchResult
} from "./algoliaSearch";
import {
  dismissActiveCallModalForCallSid,
  hasDismissedActiveCallModal,
  markActiveCallEndedForCallSid,
  readActiveCallSelection,
  storeActiveCallSelection
} from "./sessionStorage";
import { clearWrapUpSessionIfCallFinalized } from "./clearWrapUpSessionIfCallFinalized";
import { ACTIVE_CALL_PHONE_SEARCH_QUERY, type ActiveCallPhoneSearchPerson } from "./queries";

type ActiveCallContext = {
  callSid: string;
  phone: string;
  displayPhone: string;
};

type Props = {
  enabled: boolean;
};

const removeActiveCallEventParamsFromUrl = () => {
  const url = new URL(window.location.href);
  let removedParam = false;

  ["activeCall", "activeCallEnded", "phone", "callSid"].forEach((paramName) => {
    if (url.searchParams.has(paramName)) {
      url.searchParams.delete(paramName);
      removedParam = true;
    }
  });

  if (removedParam) {
    window.history.replaceState(window.history.state, "", `${url.pathname}${url.search}${url.hash}`);
  }
};

const isPhoneSearchQuery = (query: string): boolean => {
  return normalizePhoneNumber(query).length === 10;
};

const CRM_PHONE_SEARCH_LIMIT = 25;

const crmPhoneSearchPersonToHit = (person: ActiveCallPhoneSearchPerson, phone: string): PersonHit => {
  const normalizedPhone = normalizePhoneNumber(phone);
  const phoneNumber =
    person.phoneNumbers.find(({ phoneNumber, phoneFormatted }) => {
      return (
        normalizePhoneNumber(phoneNumber) === normalizedPhone ||
        normalizePhoneNumber(phoneFormatted) === normalizedPhone
      );
    }) || person.phoneNumbers[0];

  return {
    objectID: person.id,
    pga_id: person.pgaId || "",
    name: person.displayName,
    preferred_name: person.informalName || "",
    profile_photo: person.profilePhoto || undefined,
    email: person.email || undefined,
    section_name: person.primarySection?.name,
    facility_name: person.primaryFacility?.name,
    facility_id: person.primaryFacility?.id,
    phone: phoneNumber?.phoneFormatted || phoneNumber?.phoneNumber,
    legacy_id: person.pgaId || "",
    object_type: "Person"
  };
};

export default function IncomingCallEventPersonModal({ enabled }: Props) {
  const location = useLocation();
  const navigate = useNavigate();
  const client = useApolloClient();
  const flags = useFlags();
  const transcriptSummaryEnabled = !!flags["flex-call-transcript-summary"];
  const { isCompactLayout } = useIsCompactLayout();
  const index = useMemo(() => (enabled ? createActiveCallSearchIndex() : undefined), [enabled]);
  const [searchCrmPeopleByPhone] = useLazyQuery(ACTIVE_CALL_PHONE_SEARCH_QUERY, {
    fetchPolicy: "network-only"
  });
  const [callContext, setCallContext] = useState<ActiveCallContext>();
  const [query, setQuery] = useState("");
  const [people, setPeople] = useState<PersonHit[]>([]);
  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [searchErrorMessage, setSearchErrorMessage] = useState<string>();
  const [shouldRunAlgoliaFallback, setShouldRunAlgoliaFallback] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [hasManualQueryChanged, setHasManualQueryChanged] = useState(false);
  const [shouldFocusSearchInput, setShouldFocusSearchInput] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const itemRefs = useRef<Array<HTMLDivElement | null>>([]);
  const resultsContainerRef = useRef<HTMLDivElement>(null);
  const shouldDismissOnCloseRef = useRef(true);
  const shouldScrollHighlightedItemRef = useRef(false);
  const highlightedIndexRef = useRef(highlightedIndex);
  const peopleRef = useRef(people);
  const callContextRef = useRef<ActiveCallContext>();

  const navigateToPersonForCall = useCallback(
    (person: PersonHit, context: ActiveCallContext) => {
      const pgaId = person.pga_id || person.legacy_id;
      if (!pgaId) return;

      storeActiveCallSelection({
        callSid: context.callSid,
        personId: person.objectID,
        pgaId
      });
      shouldDismissOnCloseRef.current = false;
      setOpen(false);
      navigate(`/people/${pgaId}` as Path, {
        state: { openActiveCallPanel: context.callSid }
      });
    },
    [navigate]
  );

  const selectPerson = useCallback(
    (person: PersonHit) => {
      if (!callContext) return;

      navigateToPersonForCall(person, callContext);
    },
    [callContext, navigateToPersonForCall]
  );

  useEffect(() => {
    highlightedIndexRef.current = highlightedIndex;
  }, [highlightedIndex]);

  useEffect(() => {
    peopleRef.current = people;
  }, [people]);

  useEffect(() => {
    callContextRef.current = callContext;
  }, [callContext]);

  useEffect(() => {
    if (!enabled) {
      setCallContext(undefined);
      setOpen(false);
      setPeople([]);
      setIsLoading(false);
      setSearchErrorMessage(undefined);
      setShouldRunAlgoliaFallback(false);
      return;
    }

    const params = new URLSearchParams(location.search);
    const hasActiveCall = params.get("activeCall") === "true";
    const hasActiveCallEnded = params.get("activeCallEnded") === "true";
    const phone = params.get("phone");
    const callSid = params.get("callSid");

    if (hasActiveCallEnded && callSid) {
      removeActiveCallEventParamsFromUrl();
      markActiveCallEndedForCallSid(callSid);

      if (transcriptSummaryEnabled) {
        void (async () => {
          try {
            await clearWrapUpSessionIfCallFinalized(client, callSid);
          } catch {
            // Keep the synchronously marked wrap-up if the lookup fails.
          }
        })();
      }

      if (callContextRef.current?.callSid === callSid) {
        setCallContext(undefined);
        setOpen(false);
        setShouldRunAlgoliaFallback(false);
      }
      return;
    }

    if (!hasActiveCall || !phone || !callSid) return;

    removeActiveCallEventParamsFromUrl();

    const currentSelection = readActiveCallSelection();
    if (currentSelection?.callSid === callSid || hasDismissedActiveCallModal(callSid)) {
      setCallContext(undefined);
      setOpen(false);
      setShouldRunAlgoliaFallback(false);
      return;
    }

    const displayPhone = formatPhoneNumber(phone) || phone;
    storeActiveCallSelection({ callSid });
    setCallContext({ callSid, phone, displayPhone });
    setQuery(displayPhone);
    setPeople([]);
    setOpen(false);
    setHasManualQueryChanged(false);
    setShouldRunAlgoliaFallback(false);
    setShouldFocusSearchInput(false);
    setSearchErrorMessage(undefined);
    // useApolloClient() returns a stable client instance in app runtime.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- apollo client identity is stable
  }, [enabled, location.search, transcriptSummaryEnabled]);

  useEffect(() => {
    if (!callContext || hasManualQueryChanged || shouldRunAlgoliaFallback) return;

    let isCurrentSearch = true;

    const runCrmPhoneSearch = async () => {
      try {
        const result = await searchCrmPeopleByPhone({
          variables: {
            first: CRM_PHONE_SEARCH_LIMIT,
            input: {
              phone: normalizePhoneNumber(callContext.phone)
            }
          }
        });
        if (!isCurrentSearch) return;

        const crmPeople = result.data?.people.nodes || [];
        const crmPersonHits = crmPeople.map((person) => crmPhoneSearchPersonToHit(person, callContext.phone));
        if (crmPersonHits.length === 1 && crmPersonHits[0].pga_id) {
          navigateToPersonForCall(crmPersonHits[0], callContext);
          return;
        }

        if (crmPersonHits.length > 0) {
          setPeople(crmPersonHits);
          setOpen(true);
          return;
        }
      } catch {}

      if (isCurrentSearch) {
        setShouldRunAlgoliaFallback(true);
      }
    };

    runCrmPhoneSearch();

    return () => {
      isCurrentSearch = false;
    };
  }, [callContext, hasManualQueryChanged, navigateToPersonForCall, searchCrmPeopleByPhone, shouldRunAlgoliaFallback]);

  useEffect(() => {
    if (!callContext || hasManualQueryChanged || !index || !shouldRunAlgoliaFallback) return;

    setIsLoading(true);
    setSearchErrorMessage(undefined);
    let isCurrentSearch = true;

    const runInitialSearch = async () => {
      try {
        const searchResult: ActiveCallPersonSearchResult = await searchPeopleForPhone(index, callContext.phone);
        if (!isCurrentSearch) return;

        if (searchResult.directPersonMatch) {
          selectPerson(searchResult.directPersonMatch);
          return;
        }

        if (searchResult.people.length === 0) {
          setQuery("");
          setHasManualQueryChanged(true);
          setShouldFocusSearchInput(true);
        }

        setPeople(searchResult.people);
        setOpen(true);
      } catch {
        if (!isCurrentSearch) return;

        setPeople([]);
        setSearchErrorMessage("We couldn't search for the caller. Try searching manually, or cancel this call.");
        setOpen(true);
        setShouldFocusSearchInput(true);
      } finally {
        if (isCurrentSearch) {
          setIsLoading(false);
        }
      }
    };

    runInitialSearch();

    return () => {
      isCurrentSearch = false;
    };
  }, [callContext, hasManualQueryChanged, index, selectPerson, shouldRunAlgoliaFallback]);

  useEffect(() => {
    if (!open || !shouldFocusSearchInput) return;

    requestAnimationFrame(() => {
      searchInputRef.current?.focus();
      setShouldFocusSearchInput(false);
    });
  }, [open, shouldFocusSearchInput]);

  useEffect(() => {
    if (!open || !hasManualQueryChanged) return;

    const trimmedQuery = query.trim();
    if (!trimmedQuery) {
      setPeople([]);
      setIsLoading(false);
      setSearchErrorMessage(undefined);
      return;
    }

    setIsLoading(true);
    setSearchErrorMessage(undefined);
    let isCurrentSearch = true;
    const timeout = window.setTimeout(async () => {
      try {
        if (isPhoneSearchQuery(trimmedQuery) && index) {
          const phoneSearchResult = await searchPeopleForPhone(index, trimmedQuery);
          if (!isCurrentSearch) return;

          if (phoneSearchResult.people.length > 0) {
            setPeople(phoneSearchResult.people);
            return;
          }
        }

        if (!index) return;
        const querySearchResults = await searchPeopleForQuery(index, trimmedQuery);
        if (!isCurrentSearch) return;

        setPeople(querySearchResults);
      } catch {
        if (!isCurrentSearch) return;

        setPeople([]);
        setSearchErrorMessage("We couldn't complete the search. Try again, or cancel this call.");
      } finally {
        if (isCurrentSearch) {
          setIsLoading(false);
        }
      }
    }, 300);

    return () => {
      isCurrentSearch = false;
      window.clearTimeout(timeout);
    };
  }, [hasManualQueryChanged, index, open, query]);

  useEffect(() => {
    setHighlightedIndex(people.length === 1 ? 0 : -1);
    itemRefs.current = [];
  }, [people]);

  useEffect(() => {
    if (!resultsContainerRef.current) return;
    if (highlightedIndex < 0) return;
    if (!shouldScrollHighlightedItemRef.current) return;

    shouldScrollHighlightedItemRef.current = false;

    const highlightedItem = itemRefs.current[highlightedIndex];
    const parent = resultsContainerRef.current;
    if (!highlightedItem) return;

    const itemTop = highlightedItem.offsetTop;
    const itemBottom = itemTop + highlightedItem.offsetHeight;
    const parentTop = parent.scrollTop;
    const parentBottom = parentTop + parent.clientHeight;

    if (itemTop < parentTop) {
      parent.scrollTo({ top: itemTop });
    } else if (itemBottom > parentBottom) {
      parent.scrollTo({ top: itemBottom - parent.clientHeight });
    }
  }, [highlightedIndex]);

  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      const results = peopleRef.current;
      if (results.length === 0) return;

      if (event.key === "ArrowDown") {
        event.preventDefault();
        shouldScrollHighlightedItemRef.current = true;
        setHighlightedIndex((currentIndex) => (currentIndex + 1) % results.length);
        return;
      }

      if (event.key === "ArrowUp") {
        event.preventDefault();
        shouldScrollHighlightedItemRef.current = true;
        setHighlightedIndex((currentIndex) => (currentIndex - 1 + results.length) % results.length);
        return;
      }

      if (event.key === "Enter") {
        const currentIndex = highlightedIndexRef.current;
        if (currentIndex >= 0 && currentIndex < results.length) {
          event.preventDefault();
          event.stopImmediatePropagation();
          selectPerson(results[currentIndex]);
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown, true);
    return () => window.removeEventListener("keydown", handleKeyDown, true);
  }, [open, selectPerson]);

  const handleDismiss = () => {
    shouldDismissOnCloseRef.current = true;
    if (callContext) {
      dismissActiveCallModalForCallSid(callContext.callSid);
    }
    setOpen(false);
  };

  const handleDialogClosed = () => {
    if (shouldDismissOnCloseRef.current && callContext) {
      dismissActiveCallModalForCallSid(callContext.callSid);
    }

    shouldDismissOnCloseRef.current = true;
    setOpen(false);
  };

  const handleQueryChange = (value: string) => {
    setHasManualQueryChanged(true);
    setQuery(value);
  };

  const handleClear = () => {
    setHasManualQueryChanged(true);
    setQuery("");
    setPeople([]);
    setSearchErrorMessage(undefined);
    requestAnimationFrame(() => searchInputRef.current?.focus());
  };

  const trailingIcon = query ? (
    <button className="cursor-pointer flex items-center h-full" onClick={handleClear} aria-label="Clear search query">
      <Icon name="cancel" />
    </button>
  ) : undefined;

  if (!enabled || !callContext) return null;

  const resultsQuery = query.trim();
  const instructionText = (() => {
    if (searchErrorMessage) return searchErrorMessage;
    if (people.length > 1) return "We found multiple possible people. Select the caller to continue.";
    if (people.length === 1) return "We found one possible person. Select the caller to continue.";
    return "Search for the caller to continue, or cancel if they are not in the system.";
  })();

  return (
    <Dialog
      open={open}
      onClosed={handleDialogClosed}
      headline={isCompactLayout ? "Active Call" : `Active Call from ${callContext.displayPhone}`}
      actions={
        <Button variant="text" onClick={handleDismiss}>
          Cancel
        </Button>
      }
      hasFormElements
      className="w-[560px]"
    >
      <div className="md:w-[496px]">
        <div className="sticky top-0 z-10 bg-sys-brand-surface-container-low pb-4">
          {isCompactLayout && (
            <p className="mb-4 text-title-medium text-sys-brand-on-surface">From {callContext.displayPhone}</p>
          )}
          <p className="mb-6 text-body-medium text-sys-brand-on-surface-variant">{instructionText}</p>
          <TextField
            ref={searchInputRef}
            label="In Conversation With"
            value={query}
            onChangeText={handleQueryChange}
            leadingIcon="search"
            trailingIcon={trailingIcon}
            variant="outlined"
            className="w-full"
          />
        </div>
        <AlgoliaSearchResults
          results={people}
          query={resultsQuery}
          isLoading={isLoading}
          highlightedIndex={highlightedIndex}
          setHighlightedIndex={setHighlightedIndex}
          handleItemSelected={selectPerson}
          itemRefs={itemRefs}
          resultsContainerRef={resultsContainerRef}
          renderHit={createPersonOnlyHitRenderer({
            showContactDetails: true,
            showSelectButton: true
          })}
          isExactMatch={isPersonExactMatch}
          noResultsMessage={searchErrorMessage || "No results found"}
          useOptionRole={false}
          className={classNames(
            "min-h-0",
            isCompactLayout ? "overflow-visible pb-6" : "min-h-[420px] max-h-[420px] overflow-y-auto"
          )}
        />
      </div>
    </Dialog>
  );
}
